import React, {useEffect, useMemo, useState} from "react";
import {Background, MarkerType, Panel, ReactFlow} from "@xyflow/react";
import {ToggleButton} from "@mui/material";
import {FitScreen as FitScreenIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon} from "@mui/icons-material";
import {FloatingAddButton} from "@atttomyx/react-components";
import {AddConnectionDialog, EditEdgeDialog, EditNodeDialog} from "../dialogs";
import {ContactNode} from "../nodes";
import {SelectableEdge} from "../edges";
import {nodeService} from "../../services";
import {arrays, objects} from "@atttomyx/shared-utils";
import {graphUtils} from "../../utils";
import {colors, themes} from "@atttomyx/shared-constants";
import {ADD_NEW} from "../../constants";
import "@xyflow/react/dist/style.css";
import "./graphView.css";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;

const NODE_SIZE = 64;
const GRID_SIZE = 32;

const NODE_TYPES = {
    contact: ContactNode,
};

const EDGE_TYPES = {
    selectable: SelectableEdge,
};

const GraphView = (props) => {
    const { snackbar, dimensions, user, nodes, edges, horizontal, categories, people, rootId,
        onSaveNode, onSaveEdge, onDeleteNode, onDeleteEdge } = props;

    const [ showAdd, setShowAdd ] = useState(false);
    const [ showEdit, setShowEdit ] = useState(false);
    const [ edge, setEdge ] = useState(null);
    const [ node, setNode ] = useState(null);
    const [ selected, setSelected ] = useState(null);
    const [ instance, setInstance ] = useState(null);
    const [ zoom, setZoom ] = useState(1);

    const dark = useMemo(() => {
        return user.settings.theme === themes.DARK;
    }, [user]);

    const colorMode = useMemo(() => {
        return dark ? "dark" : "light";
    }, [dark]);

    const gridType = useMemo(() => {
        return dark ? "dots" : "lines";
    }, [dark]);

    const gridColor = useMemo(() => {
        return dark ? colors.LIGHT_SLATE_GREY : "#C1D5EF";
    }, [dark]);

    const edgeStyle = useMemo(() => {
        return dark ? {
            stroke: colors.WHITE,
            strokeWidth: 2,
            opacity: 0.5,
        } : {
            stroke: colors.BLACK,
            strokeWidth: 2,
            opacity: 0.3,
        }
    }, [dark]);

    const markerEndStyle = useMemo(() => {
        return dark ? {
            type: MarkerType.Arrow,
            color: colors.WHITE,
            opacity: 0.5,
        } : {
            type: MarkerType.Arrow,
            color: colors.BLACK,
            opacity: 0.3,
        }
    }, [edgeStyle]);

    const labelStyle = useMemo(() => {
        return dark ? {
            fill: colors.WHITE,
        } : {
            fill: colors.BLACK,
        }
    }, [edgeStyle]);

    const idToEdge = useMemo(() => {
        return arrays.getIdToEntity(edges);
    }, [edges]);

    const graphNodes = useMemo(() => {
        const ret = [];

        nodes.forEach((node) => {
            const decorated = objects.deepCopy(node);

            decorated.horizontal = horizontal;

            decorated.setAddConnection = (edge) => {
                setShowAdd(true);
                setEdge(edge);
            };

            arrays.addTo(ret, {
                id: node.id,
                type: "contact",
                data: decorated,
            });
        });

        return ret;
    }, [user, horizontal, nodes, setEdge, setShowAdd]);

    const graphEdges = useMemo(() => {
        const ret = [];

        edges.forEach((edge) => {
            arrays.addTo(ret, {
                id: edge.id,
                type: "selectable",
                source: edge.sourceNodeId,
                target: edge.targetNodeId,
                style: edgeStyle,
                markerEnd: markerEndStyle,
                label: edge.label,
                labelStyle: labelStyle,
                labelShowBg: false,
            })
        });

        return ret;
    }, [edges]);

    const graph = useMemo(() => {
        return horizontal
            ? graphUtils.applyHorizontalLayout(graphNodes, graphEdges, NODE_SIZE)
            : graphUtils.applyVerticalLayout(graphNodes, graphEdges, NODE_SIZE);
    }, [graphNodes, graphEdges, horizontal]);

    useEffect(() => {
        setTimeout(fitView);
    }, [graph, instance]);

    useEffect(() => {
        setTimeout(fitView);
    }, [dimensions.landscape]);

    const syncZoom = () => {
        if (instance) {
            setZoom(instance.getZoom());
        }
    }

    const fitView = () => {
        if (instance) {
            instance.fitView();
            syncZoom();
        }
    };

    const zoomIn = () => {
        if (instance) {
            instance.zoomIn().then(syncZoom);
        }
    };

    const zoomOut = () => {
        if (instance) {
            instance.zoomOut().then(syncZoom);
        }
    };

    const createRootNode = () => {
        const success = (saved) => {
            onSaveNode(saved);
            setShowAdd(true);
            setEdge({
                sourceNodeId: saved.id,
                targetNodeId: ADD_NEW,
            });
        };

        nodeService.createRootNode(success, snackbar.setError);
    };

    const deselectNode = (nodeId) => {
        const element = instance.getInternalNode(nodeId);

        if (element) {
            element.selected = false;
        }
    };

    const deselectEdge = (edgeId) => {
        const element = instance.getEdge(edgeId);

        if (element) {
            element.selected = false;
            element.animated = false;
        }
    };

    const selectNode = (nodeId) => {
        const element = instance.getInternalNode(nodeId);

        if (element) {
            element.selected = true;
        }
    };

    const selectEdge = (edgeId) => {
        const element = instance.getEdge(edgeId);

        if (element) {
            element.selected = true;
            element.animated = true;
        }
    };

    const syncSelected = (elementId) => {
        if (selected !== elementId) {
            deselectNode(selected);
            deselectEdge(selected);
        }

        setSelected(elementId);
    };

    const clickNode = (node) => {
        const nodeId = node.id;

        syncSelected(nodeId);
        selectNode(nodeId);
        setShowEdit(true);
        setNode(node.data);
    };

    const clickEdge = (edge) => {
        const edgeId = edge.id;

        syncSelected(edgeId);
        selectEdge(edgeId);
        setShowEdit(true);
        setEdge(idToEdge[edgeId]);
    };

    const clickPane = () => {
        if (selected) {
            deselectNode(selected);
            deselectEdge(selected);
            setSelected(null);
        }
    };

    const resetDialogs = () => {
        setShowAdd(false);
        setShowEdit(false);
        setEdge(null);
        setNode(null);
    };

    return <div className="graph-view">
        <ReactFlow
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            nodes={graph.nodes}
            edges={graph.edges}
            elementsSelectable={true}
            nodesFocusable={false}
            nodesDraggable={false}
            nodesConnectable={false}
            edgesFocusable={false}
            onNodeClick={(event, node) => clickNode(node)}
            onEdgeClick={(event, edge) => clickEdge(edge)}
            onPaneClick={(event) => clickPane()}
            onInit={setInstance}
            fitView={true}
            colorMode={colorMode}
            minZoom={ZOOM_MIN}
            maxZoom={ZOOM_MAX}
        >
            <Background
                variant={gridType}
                bgColor={colors.TRANSPARENT}
                color={gridColor}
                gap={GRID_SIZE}
            />
            <Panel
                position="bottom-left"
                className="toggles"
            >
                <ToggleButton
                    value="zoom-out"
                    title="Zoom out"
                    onClick={zoomOut}
                    disabled={zoom <= ZOOM_MIN}
                >
                    <ZoomOutIcon/>
                </ToggleButton>
                <ToggleButton
                    value="zoom-to-fit"
                    title="Zoom to fit"
                    onClick={fitView}
                >
                    <FitScreenIcon/>
                </ToggleButton>
                <ToggleButton
                    value="zoom-in"
                    title="Zoom in"
                    onClick={zoomIn}
                    disabled={zoom >= ZOOM_MAX}
                >
                    <ZoomInIcon/>
                </ToggleButton>
            </Panel>
            <Panel
                position="top-left"
            >
                <p>TODO: filters, search, etc.</p>
            </Panel>
        </ReactFlow>
        <FloatingAddButton
            title="Add connection"
            position={!dimensions.landscape || !dimensions.short ? "higher" : undefined}
            center={false}
            onClick={() => {
                if (nodes.length === 0) {
                    createRootNode();

                } else {
                    setShowAdd(true);
                    setEdge({
                        sourceNodeId: rootId,
                        targetNodeId: ADD_NEW,
                    });
                }
            }}
        />
        {showAdd && edge ?
            <AddConnectionDialog
                snackbar={snackbar}
                horizontal={horizontal}
                categories={categories}
                people={people}
                edge={edge}
                onCancel={resetDialogs}
                onSaveNode={onSaveNode}
                onSaveEdge={(saved) => {
                    resetDialogs();
                    onSaveEdge(saved);
                }}
            /> : showEdit && edge ?
            <EditEdgeDialog
                snackbar={snackbar}
                horizontal={horizontal}
                categories={categories}
                people={people}
                edge={edge}
                onCancel={resetDialogs}
                onSaveEdge={(saved) => {
                    resetDialogs();
                    onSaveEdge(saved);
                }}
                onDeleteEdge={(deletedId) => {
                    resetDialogs();
                    onDeleteEdge(deletedId);
                }}
            /> : showEdit && node ?
            <EditNodeDialog
                snackbar={snackbar}
                user={user}
                node={node}
                onCancel={resetDialogs}
                onSaveNode={(saved) => {
                    resetDialogs();
                    onSaveNode(saved);
                }}
                onDeleteNode={(deletedId) => {
                    resetDialogs();
                    onDeleteNode(deletedId);
                }}
                showActions={true}
            /> : null}
    </div>
};

export default GraphView;
